import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Camera, Wind, ExternalLink } from "lucide-react";

const WEBCAM_URL = "https://panoramicams.com/porto-pollo-rupis/";
const WINDY_EMBED = "https://embed.windy.com/embed.html?type=forecast&location=coordinates&metricRain=mm&metricTemp=%C2%B0C&metricWind=kt&zoom=12&overlay=wind&product=ecmwf&level=surface&lat=41.193&lon=9.319&detailLat=41.193&detailLon=9.319&marker=true&message=true";

const tabs = {
  it: { webcam: "Webcam Live", wind: "Vento & Meteo", title: "Porto Pollo Live", openWebcam: "Apri Webcam", windInfo: "Previsioni in tempo reale da Windy.com" },
  en: { webcam: "Live Webcam", wind: "Wind & Weather", title: "Porto Pollo Live", openWebcam: "Open Webcam", windInfo: "Real-time forecast from Windy.com" },
  fr: { webcam: "Webcam Live", wind: "Vent & Meteo", title: "Porto Pollo Live", openWebcam: "Ouvrir Webcam", windInfo: "Previsions en temps reel de Windy.com" },
  de: { webcam: "Live Webcam", wind: "Wind & Wetter", title: "Porto Pollo Live", openWebcam: "Webcam offnen", windInfo: "Echtzeit-Vorhersage von Windy.com" },
  es: { webcam: "Webcam en Vivo", wind: "Viento & Clima", title: "Porto Pollo Live", openWebcam: "Abrir Webcam", windInfo: "Pronostico en tiempo real de Windy.com" },
};

export const LivePanel = ({ isOpen, onClose, language = "it" }) => {
  const [activeTab, setActiveTab] = useState("wind");
  const t = tabs[language] || tabs.it;

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
      >
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
        <motion.div
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{ type: "spring", damping: 30, stiffness: 300 }}
          className="relative w-full sm:max-w-2xl max-h-[92vh] bg-slate-900 rounded-t-2xl sm:rounded-2xl overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <h2 className="font-bold text-white text-lg" data-testid="live-panel-title">{t.title}</h2>
            <button onClick={onClose} data-testid="live-panel-close" className="p-1.5 rounded-full hover:bg-white/10 text-white/70 hover:text-white transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-white/10">
            <button
              onClick={() => setActiveTab("wind")}
              data-testid="live-tab-wind"
              className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-all ${
                activeTab === "wind" ? "text-cyan-400 border-b-2 border-cyan-400 bg-white/5" : "text-white/50 hover:text-white/70"
              }`}
            >
              <Wind className="w-4 h-4" />
              {t.wind}
            </button>
            <button
              onClick={() => setActiveTab("webcam")}
              data-testid="live-tab-webcam"
              className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-all ${
                activeTab === "webcam" ? "text-cyan-400 border-b-2 border-cyan-400 bg-white/5" : "text-white/50 hover:text-white/70"
              }`}
            >
              <Camera className="w-4 h-4" />
              {t.webcam}
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden">
            {activeTab === "wind" && (
              <div className="h-full flex flex-col">
                <p className="text-xs text-white/40 text-center py-2">{t.windInfo}</p>
                <div className="flex-1" style={{ minHeight: "450px" }}>
                  <iframe
                    src={WINDY_EMBED}
                    title="Windy Forecast Porto Pollo"
                    className="w-full h-full border-0"
                    style={{ minHeight: "450px" }}
                    data-testid="windy-iframe"
                    allow="geolocation"
                  />
                </div>
              </div>
            )}

            {activeTab === "webcam" && (
              <div className="h-full flex flex-col">
                <div className="flex-1 relative" style={{ minHeight: "450px" }}>
                  <iframe
                    src={WEBCAM_URL}
                    title="Porto Pollo Webcam"
                    className="w-full h-full border-0"
                    style={{ minHeight: "450px" }}
                    data-testid="webcam-iframe"
                    sandbox="allow-scripts allow-same-origin"
                  />
                </div>
                <div className="p-3 border-t border-white/10">
                  <a
                    href={WEBCAM_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="webcam-external-link"
                    className="flex items-center justify-center gap-2 w-full py-2.5 bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    {t.openWebcam}
                  </a>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default LivePanel;
